import { Alert } from '@creditornot/cb-alert';
import { Button } from '@creditornot/cb-button';
import { colors } from '@creditornot/cb-ingredients';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cb-modal';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import { DateTime, Interval } from 'luxon';
import React, { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { WoltModal } from 'common/components';
import { Typography } from 'common/typography';
import { localize } from 'common/utils';

import { useBestOfWoltData } from '../hooks/useBestOfWoltData';
import { useBestOfWoltModal } from '../hooks/useBestOfWoltModal';
import BestOfWoltStar from '../images/BestOfWoltStar.png';
import CelebrationImage from '../images/BoWCelebration.png';
import { RelativeMonth } from '../types';

type CelebrationProps = {
  venue: Venue;
};

const CelebrationModalHeader = styled(ModalHeader)`
  background-image: url(${CelebrationImage});
  height: 325px;
  display: flex;
  align-items: flex-start;
  background-position: center;
  border-block-end: none;
  position: relative;
`;

const BoWStarImageContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  position: absolute;
  bottom: 0;
  right: 0;
`;

const BowStarImage = styled.img`
  height: 230px;
  filter: ${cssThemedValue({ light: `drop-shadow(0 8px 8px ${colors.pepper12})`, dark: 'none' })};
`;

const CelebrationModalContent = styled(ModalContent)`
  display: flex;
  padding: 48px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  max-width: 620px;
`;

const CelebrationModalFooter = styled(ModalFooter)`
  display: flex;
  justify-content: flex-end;
`;

export const Celebration = ({ venue }: CelebrationProps) => {
  const { shouldShowModal, resetModal, hideModal, navigateToBestOfWolt } = useBestOfWoltModal({
    key: 'best_of_wolt.celebrate',
    venue,
  });
  const { locale } = useIntl();
  const now = DateTime.now().setZone(venue.timezone);
  const firstFiveDaysOfMonthInterval = Interval.fromDateTimes(
    now.set({ day: 1 }).startOf('day'),
    now.set({ day: 5 }).endOf('day'),
  );
  const isFirstFiveDaysOfMonth = firstFiveDaysOfMonthInterval.contains(now);

  useEffect(() => {
    if (!isFirstFiveDaysOfMonth) {
      resetModal();
    }
  }, [isFirstFiveDaysOfMonth, resetModal]);

  const { relativeDate, data, isLoading } = useBestOfWoltData({
    venueId: venue.id,
    venueTimezone: venue.timezone,
    relativeMonth: RelativeMonth.THIS_MONTH,
    enabled: isFirstFiveDaysOfMonth && shouldShowModal,
  });

  const shouldShowCelebrateModal =
    isFirstFiveDaysOfMonth && shouldShowModal && !isLoading && data?.data.calculationStatus.details?.isBoW === true;

  return (
    <WoltModal isOpen={shouldShowCelebrateModal}>
      <CelebrationModalHeader onCloseClick={() => hideModal()}>
        <BoWStarImageContainer>
          <BowStarImage src={BestOfWoltStar} />
        </BoWStarImageContainer>
      </CelebrationModalHeader>
      <CelebrationModalContent>
        <Typography.Heading3>
          <FormattedMessage id="best-of-wolt.celebrate.congratulations" />
        </Typography.Heading3>
        <Typography.Body1>
          <p>
            <FormattedMessage
              id="best-of-wolt.celebrate.description"
              values={{
                venue: localize(venue.name, locale),
              }}
            />
          </p>
        </Typography.Body1>
        <Alert variant="success">
          <Typography.Body3>
            <FormattedMessage
              id="best-of-wolt.celebrate.active"
              values={{
                date: relativeDate.setLocale(locale).endOf('month').toLocaleString(DateTime.DATE_MED),
              }}
            />
          </Typography.Body3>
        </Alert>
      </CelebrationModalContent>
      <CelebrationModalFooter>
        <Button variant="blue" size="small" onClick={() => navigateToBestOfWolt()}>
          <FormattedMessage id="best-of-wolt.celebrate.learn" />
        </Button>
      </CelebrationModalFooter>
    </WoltModal>
  );
};
